import { useSnackbar } from 'notistack';
import { useState } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Divider, Typography, MenuItem, styled, Button, Badge } from '@mui/material';
// hooks
import { useNavigate } from 'react-router';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import CustomModal from '../../../components/CustomModal';
import ChangePassword from '../../../pages/auth/ChangePassword';
import { useDispatch, useSelector } from '../../../redux/store';
import { closeChangePwd, openChangePwd } from '../../../redux/slices/authPages';
import useLocales from '../../../hooks/useLocales';
import { fNumberDecimal } from '../../../utils/formatNumber';
import Iconify from '../../../components/Iconify';
import userLock from '../../../assets/user-lock.png';
import deposit from '../../../assets/profile/deposit.png';
import withdraw from '../../../assets/profile/withdraw.png';
import Image from '../../../components/Image';
import BankDetails from '../../../assets/profile/bank-details.png';
import BetHistory from '../../../assets/profile/bet-history.png';
import TransactionHistory from '../../../assets/profile/transaction-history.png';
import Notification from '../../../assets/profile/notification.png';
import EditProfile from '../../../assets/profile/edit-profile.png';
import TransferIcon from '../../../assets/profile/transfer.png';

import { openModal } from '../../../redux/slices/profileSlice';

// ----------------------------------------------------------------------

const MenuText = styled(Typography)(({ theme }) => ({
  color: '#555D74',
  fontWeight: 500,
  fontSize: '16px',
}));

export default function AccountPopover() {
  const theme = useTheme();

  const navigate = useNavigate();

  const { userProfile, logout } = useAuth();

  const { translate } = useLocales();

  const { isOpenChangePwd } = useSelector((x) => x.authPages);

  const { memberBalance } = useSelector((x) => x.lookup);

  const dispatch = useDispatch();

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ml: 0.5,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <Image
          src={require('../../../assets/side-nav/profile.png')}
          sx={{ width: 30, height: 30, cursor: 'pointer' }}
        />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          width: 300,
          borderRadius: 1,
          overflow: 'hidden',
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 1.5,
          },
          background: theme.palette.background.default,
        }}
        disabledArrow
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            px: 2,
            py: 2,
          }}
        >
          <Button
            fullWidth
            startIcon={<Image src={deposit} />}
            sx={{
              backgroundColor: (theme) => alpha(theme.palette.primary.light, 0.2),
              color: 'primary.contrastText',
              fontWeight: 600,
              borderRadius: 1,
              textTransform: 'none',
              py: 1,
              '&:hover': { backgroundColor: 'primary.main' },
            }}
            onClick={() => {
              navigate('/deposit');
              handleClose();
            }}
          >
            {translate('deposit')}
          </Button>

          <Button
            fullWidth
            startIcon={<Image src={withdraw} />}
            sx={{
              backgroundColor: (theme) => alpha(theme.palette.primary.light, 0.2),
              color: 'primary.contrastText',
              fontWeight: 600,
              borderRadius: 1,
              textTransform: 'none',
              py: 1,
              '&:hover': { backgroundColor: 'primary.main' },
            }}
            onClick={() => {
              navigate('/withdraw');
              handleClose();
            }}
          >
            {translate('withdraw')}
          </Button>
        </Box>

        <MenuItem
          onClick={() => {
            navigate('/transfer');
            handleClose();
          }}
          sx={{
            py: '16px',
          }}
        >
          <Image src={TransferIcon} sx={{ width: '24px', height: '24px', mr: '8px' }} />
          <MenuText>{translate('transfer')}</MenuText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            // navigate('/user/account');
            dispatch(openModal({ view: 'transaction-history' }));
            handleClose();
          }}
          sx={{
            py: '16px',
          }}
        >
          <Image src={TransactionHistory} sx={{ width: '24px', height: '24px', mr: '8px' }} />
          <MenuText>{translate('transaction_history')}</MenuText>
        </MenuItem>

        {/* <MenuItem
          onClick={() => {
            // navigate('/user/banks');
            dispatch(
              openModal({
                view: 'bet-history',
              })
            );
            handleClose();
          }}
          sx={{
            py: '16px',
          }}
        >
          <Image src={BetHistory} sx={{ width: '24px', height: '24px', mr: '8px' }} />
          <MenuText>Bet History</MenuText>
        </MenuItem> */}

        <MenuItem
          onClick={() => {
            dispatch(
              openModal({
                view: 'bank-details',
              })
            );
            handleClose();
          }}
          sx={{
            py: '16px',
          }}
        >
          <Image src={BankDetails} sx={{ width: '24px', height: '24px', mr: '8px' }} />
          <MenuText>{translate('bank_details')}</MenuText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            dispatch(
              openModal({
                view: 'edit-profile',
              })
            );
            handleClose();
          }}
          sx={{
            py: '16px',
          }}
        >
          <Image src={EditProfile} sx={{ width: '24px', height: '24px', mr: '8px' }} />
          <MenuText>{translate('edit_profile')}</MenuText>
        </MenuItem>
        {/* 
        <MenuItem
          onClick={() => {
            navigate('/referral');
            handleClose();
          }}
        >
          <Iconify icon="mdi:account-plus-outline" sx={{ mr: 1, width: 20, height: 20 }} />
          <MenuText>Referral</MenuText>
        </MenuItem> */}

        {/* <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: '#ccc',
            mx: 1,
          }}
        /> */}

        {/* <MenuItem
          onClick={() => {
            dispatch(openLogout());
            handleClose();
          }}
          sx={{ m: 1 }}
        >
          <Iconify icon="mdi:logout-variant" sx={{ mr: 1, width: 20, height: 20 }} />
          <MenuText>Logout</MenuText>
        </MenuItem> */}
      </MenuPopover>
    </>
  );
}
